import React, { useState } from "react";
import { Link } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import styles from "./ShareTheLoadWaitlist.module.css";

const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.logoIcon}>
    <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={styles.successIcon}>
    <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const features = [
  {
    icon: "✅",
    title: "Delegate Chores",
    description: "Assign tasks to family members and track completion — no more nagging.",
  },
  {
    icon: "📅",
    title: "Shared Calendar",
    description: "Scan event flyers with your camera and share them instantly with the family.",
  },
  {
    icon: "🛒",
    title: "Shopping List",
    description: "One shared list everyone can add to and check off in real time.",
  },
  {
    icon: "🍽️",
    title: "Meal Planning",
    description: "Plan the week together so nobody has to answer 'what's for dinner?'",
  },
];

type FormState = "idle" | "loading" | "success" | "error";

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const ShareTheLoadWaitlist: React.FC = () => {
  const [email, setEmail] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [emailError, setEmailError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setFormState("loading");
    try {
      await addDoc(collection(db, "sharetheload-waitlist"), {
        email: trimmed,
        submittedAt: serverTimestamp(),
      });
      setFormState("success");
    } catch {
      setFormState("error");
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.backgroundGlow} />

      <nav className={styles.nav}>
        <div className={styles.navContainer}>
          <Link to="/share-the-load" className={styles.logo}>
            <LogoIcon />
            <span>Share the Load</span>
          </Link>
        </div>
      </nav>

      <main id="main-content" className={styles.main} role="main" tabIndex={-1}>
        <div className={styles.container}>
          <div className={styles.badge}>Coming Soon to iOS</div>

          <h1 className={styles.headline}>
            Your family,<br />finally in sync.
          </h1>
          <p className={styles.subheadline}>
            Share the Load puts chores, events, shopping, and meal planning in one place — so your whole family stays coordinated without the chaos.
          </p>

          {formState === "success" ? (
            <div className={styles.successCard}>
              <CheckCircleIcon />
              <h2 className={styles.successTitle}>You're on the list!</h2>
              <p className={styles.successText}>
                We'll send you one email when Share the Load launches on iOS. No spam, ever.
              </p>
              <Link to="/share-the-load" className={styles.backLink}>
                Back to Share the Load
              </Link>
            </div>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.inputRow}>
                <div className={styles.inputWrapper}>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    className={`${styles.input} ${emailError ? styles.inputError : ""}`}
                    aria-label="Email address"
                    aria-describedby={emailError ? "email-error" : undefined}
                    disabled={formState === "loading"}
                    autoComplete="email"
                  />
                  {emailError && (
                    <p id="email-error" className={styles.errorText} role="alert">
                      {emailError}
                    </p>
                  )}
                </div>
                <button type="submit" className={styles.submitButton} disabled={formState === "loading"}>
                  {formState === "loading" ? (
                    <span className={styles.spinner} aria-hidden="true" />
                  ) : (
                    "Notify Me"
                  )}
                </button>
              </div>

              <p className={styles.consent}>
                By signing up you agree to receive one launch notification. No spam. Unsubscribe by emailing{" "}
                <a href="mailto:dixonappdevelopment@gmail.com" className={styles.consentLink}>
                  dixonappdevelopment@gmail.com
                </a>
                .
              </p>

              {formState === "error" && (
                <p className={styles.errorText} role="alert">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}
            </form>
          )}

          <div className={styles.features}>
            {features.map((f) => (
              <div key={f.title} className={styles.featureCard}>
                <div className={styles.featureEmoji}>{f.icon}</div>
                <h3 className={styles.featureTitle}>{f.title}</h3>
                <p className={styles.featureDescription}>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className={styles.footer}>
        <p>
          &copy; {new Date().getFullYear()} Dixon App Development &middot;{" "}
          <Link to="/" className={styles.footerLink}>
            Dixon App Development
          </Link>
        </p>
      </footer>
    </div>
  );
};
